import { uiText } from '../../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from '../common/Dialog';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronRight,
  ClipboardList,
  FileText,
  Search,
  X,
} from "lucide-react";
import { templatesApi, getApiErrorMessage } from "../../api";
import { formatAuditType, formatDate } from "../../utils/formatters";

const QUESTION_TYPE_LABELS = {
  text: uiText("Текст"),
  yesno: uiText("Да / Нет"),
  number: uiText("Число"),
  select: uiText("Выбор из списка"),
  photo: uiText("Файл или фото"),
  signature: uiText("Подпись"),
};

const ANSWER_SCOPE_LABELS = {
  auditor: uiText("Отвечает аудитор"),
  customer: uiText("Отвечает проверяемый"),
  both: uiText("Могут отвечать обе стороны"),
};

function isCompatible(template, auditKind) {
  const kinds = Array.isArray(template?.applies_to_kinds)
    ? template.applies_to_kinds
    : [];
  return !auditKind || kinds.length === 0 || kinds.includes(auditKind);
}

function versionLabel(value) {
  if (!value) return uiText("Версия не указана");
  return String(value).toLowerCase().startsWith("v") ? String(value) : `v${value}`;
}

export default function TemplatePickerModal({
  templates = [],
  selectedId = "",
  auditKind = "",
  auditKindLabel = "",
  onSelect,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const searchRef = useRef(null);
  const mountedRef = useRef(true);
  const detailRequestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    searchRef.current?.focus();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleEscape = () => {
      if (activeTemplateId) {
        setActiveTemplateId(null);
        setDetailError("");
      } else {
        onClose?.();
      }
  };

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ru-RU");
    const matches = normalizedQuery
      ? templates.filter((item) => (
        String(item.name || "").toLocaleLowerCase("ru-RU").includes(normalizedQuery)
      ))
      : [...templates];

    return matches.sort((left, right) => {
      const leftSelected = String(left.id) === String(selectedId) ? 0 : 1;
      const rightSelected = String(right.id) === String(selectedId) ? 0 : 1;
      if (leftSelected !== rightSelected) return leftSelected - rightSelected;
      const leftCompatible = isCompatible(left, auditKind) ? 0 : 1;
      const rightCompatible = isCompatible(right, auditKind) ? 0 : 1;
      return leftCompatible - rightCompatible;
    });
  }, [auditKind, query, selectedId, templates]);

  const compatibleCount = useMemo(
    () => templates.filter((item) => isCompatible(item, auditKind)).length,
    [auditKind, templates],
  );

  const openDetails = async (template) => {
    setActiveTemplateId(template.id);
    setDetailError("");
    if (detailsById[template.id]) {
      setDetailLoading(false);
      return;
    }

    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setDetailLoading(true);
    try {
      const response = await templatesApi.get(template.id);
      if (!mountedRef.current) return;
      setDetailsById((current) => ({ ...current, [template.id]: response.data }));
    } catch (error) {
      if (!mountedRef.current || detailRequestRef.current !== requestId) return;
      setDetailError(getApiErrorMessage(error) || uiText("Не удалось загрузить содержимое шаблона"));
    } finally {
      if (mountedRef.current && detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };

  const chooseTemplate = (template) => {
    if (!isCompatible(template, auditKind)) return;
    onSelect?.(String(template.id));
    onClose?.();
  };

  const catalogItem = templates.find(
    (item) => String(item.id) === String(activeTemplateId),
  );
  const activeTemplate = detailsById[activeTemplateId] || catalogItem;
  const activeCompatible = activeTemplate
    ? isCompatible(activeTemplate, auditKind)
    : false;
  const sections = Array.isArray(activeTemplate?.sections)
    ? activeTemplate.sections
    : [];
  const questionsCount = sections.reduce(
    (total, section) => total + (Array.isArray(section.questions) ? section.questions.length : 0),
    0,
  );

  const modal = (
    <Dialog size="catalog" labelledBy="template-picker-title" onClose={onClose} onEscape={handleEscape} initialFocusRef={searchRef}>
        <header className="ap-dialog__header">
          <div className="template-picker-heading">
            <span className="template-picker-heading-icon" aria-hidden="true">
              <ClipboardList size={18} />
            </span>
            <div>
              <div className="ap-dialog__title" id="template-picker-title">
                {activeTemplateId ? uiText("Просмотр чек-листа") : uiText("Выбор чек-листа аудита")}
              </div>
              <p>
                {activeTemplateId
                  ? uiText("Проверьте состав чек-листа перед выбором.")
                  : uiText("{v0} опубликованных шаблонов · {v1} подходят для выбранного типа аудита", {v0: (templates.length), v1: (compatibleCount)})}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="ap-dialog__close"
            aria-label={uiText("Закрыть выбор шаблона")}
            title={uiText("Закрыть")}
            onClick={onClose}
          >
            <X size={17} />
          </button>
        </header>

        {activeTemplateId ? (
          <>
            <div className="ap-dialog__body template-picker-preview">
              <button
                type="button"
                className="template-picker-back"
                onClick={() => {
                  setActiveTemplateId(null);
                  setDetailError("");
                }}
              >
                <ArrowLeft size={15} />{uiText(" К списку шаблонов")}</button>

              {detailLoading ? (
                <div className="template-picker-loading" role="status">
                  <div className="spinner" />
                  <span>{uiText("Загружаем структуру шаблона…")}</span>
                </div>
              ) : detailError ? (
                <div className="alert alert-error">{detailError}</div>
              ) : activeTemplate ? (
                <>
                  <div className="template-preview-hero">
                    <div className="template-preview-title-row">
                      <div>
                        <h3>{activeTemplate.name}</h3>
                        <div className="template-preview-meta">
                          <span className="template-version-badge">{versionLabel(activeTemplate.version)}</span>
                          <span><CalendarDays size={14} />{uiText(" Создан ")}{formatDate(activeTemplate.created_at)}</span>
                          <span>{formatAuditType(activeTemplate.audit_type)}</span>
                        </div>
                      </div>
                      {String(activeTemplate.id) === String(selectedId) ? (
                        <span className="template-selected-badge"><Check size={14} />{uiText(" Выбран")}</span>
                      ) : null}
                    </div>
                    {activeTemplate.description ? <p>{activeTemplate.description}</p> : null}
                    <div className="template-preview-facts">
                      <div><strong>{sections.length}</strong><span>{uiText("разделов")}</span></div>
                      <div><strong>{questionsCount}</strong><span>{uiText("вопросов")}</span></div>
                      <div><strong>{activeTemplate.language || "RU"}</strong><span>{uiText("язык")}</span></div>
                      <div><strong>{activeTemplate.author_name || "—"}</strong><span>{uiText("автор")}</span></div>
                    </div>
                  </div>

                  {!activeCompatible ? (
                    <div className="template-compatibility-note is-warning">{uiText("Этот шаблон не предназначен для типа «")}{auditKindLabel || auditKind}{uiText("». Его можно посмотреть, но нельзя выбрать для текущего аудита.")}</div>
                  ) : null}

                  <div className="template-preview-sections">
                    <div className="template-preview-section-heading">{uiText("Состав чек-листа")}</div>
                    {sections.length ? sections.map((section, sectionIndex) => (
                      <details className="template-preview-section" key={section.id || `${section.title}-${sectionIndex}`}>
                        <summary>
                          <span>{sectionIndex + 1}. {section.title}</span>
                          <span>{section.questions?.length || 0}{uiText(" вопросов")}</span>
                        </summary>
                        <div className="template-preview-questions">
                          {(section.questions || []).map((question, questionIndex) => (
                            <div className="template-preview-question" key={question.id || `${question.text}-${questionIndex}`}>
                              <span className="template-preview-question-number">{questionIndex + 1}</span>
                              <div>
                                <div className="template-preview-question-text">{question.text}</div>
                                <div className="template-preview-question-meta">
                                  <span>{QUESTION_TYPE_LABELS[question.question_type] || question.question_type}</span>
                                  <span>{ANSWER_SCOPE_LABELS[question.answer_scope] || uiText("Отвечает аудитор")}</span>
                                  {question.required ? <span>{uiText("Обязательный")}</span> : null}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )) : (
                      <div className="template-picker-empty compact">{uiText("В шаблоне пока нет разделов.")}</div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
            <footer className="ap-dialog__footer">
              <button type="button" className="ap-button ap-button--secondary" onClick={onClose}>{uiText("Отмена")}</button>
              <button
                type="button"
                className="ap-button ap-button--primary"
                disabled={!activeTemplate || detailLoading || !activeCompatible}
                onClick={() => chooseTemplate(activeTemplate)}
              >
                <Check size={15} />
                {String(activeTemplate?.id) === String(selectedId) ? uiText("Оставить выбранным") : uiText("Выбрать шаблон")}
              </button>
            </footer>
          </>
        ) : (
          <>
            <div className="ap-dialog__body">
              <div className="template-picker-toolbar">
                <div className="template-picker-search">
                  <Search size={18} aria-hidden="true" />
                  <input
                    ref={searchRef}
                    type="search"
                    className="ap-control"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={uiText("Поиск по названию шаблона")}
                    aria-label={uiText("Поиск по названию шаблона")}
                  />
                  {query ? (
                    <button type="button" aria-label={uiText("Очистить поиск")} onClick={() => setQuery("")}>
                      <X size={15} />
                    </button>
                  ) : null}
                </div>
                <div className="template-picker-result-count">{uiText("Найдено: ")}<strong>{filteredTemplates.length}</strong>
                </div>
              </div>

              <div className="template-picker-compatibility-hint">{uiText("В списке показаны все опубликованные шаблоны. Неподходящие для типа «")}{auditKindLabel || auditKind || uiText("не выбран")}{uiText("» доступны для просмотра, но не для выбора.")}</div>

              {filteredTemplates.length ? (
                <div className="template-picker-list">
                  {filteredTemplates.map((template) => {
                    const compatible = isCompatible(template, auditKind);
                    const selected = String(template.id) === String(selectedId);
                    return (
                      <article
                        className={`template-picker-card${selected ? " is-selected" : ""}${compatible ? "" : " is-incompatible"}`}
                        key={template.id}
                      >
                        <button
                          type="button"
                          className="template-picker-card-main"
                          onClick={() => openDetails(template)}
                          aria-label={uiText("Просмотреть шаблон {v0}, {v1}", {v0: (template.name), v1: (versionLabel(template.version))})}
                        >
                          <span className="template-picker-file-icon" aria-hidden="true"><FileText size={18} /></span>
                          <span className="template-picker-card-content">
                            <span className="template-picker-card-title-row">
                              <span className="template-picker-card-title">{template.name}</span>
                              {selected ? <span className="template-selected-badge"><Check size={13} />{uiText(" Выбран")}</span> : null}
                            </span>
                            <span className="template-picker-card-meta">
                              <span className="template-version-badge">{versionLabel(template.version)}</span>
                              <span><CalendarDays size={13} /> {formatDate(template.created_at)}</span>
                              {Array.isArray(template.applies_to_kinds) && template.applies_to_kinds.length === 0
                                ? <span>{uiText("Универсальный")}</span>
                                : null}
                            </span>
                            {!compatible ? <span className="template-incompatible-label">{uiText("Не подходит для выбранного типа аудита")}</span> : null}
                          </span>
                          <ChevronRight size={18} className="template-picker-card-chevron" aria-hidden="true" />
                        </button>
                        <div className="template-picker-card-action">
                          <button
                            type="button"
                            className={selected ? "ap-button ap-button--secondary" : "ap-button ap-button--primary"}
                            disabled={!compatible}
                            onClick={() => chooseTemplate(template)}
                          >
                            {selected ? <><Check size={14} />{uiText(" Выбран")}</> : uiText("Выбрать")}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="template-picker-empty">
                  <Search size={24} />
                  <strong>{uiText("Шаблоны не найдены")}</strong>
                  <span>{uiText("Проверьте название или очистите поисковый запрос.")}</span>
                  <button type="button" className="ap-button ap-button--secondary" onClick={() => setQuery("")}>{uiText("Очистить поиск")}</button>
                </div>
              )}
            </div>
            <footer className="ap-dialog__footer">
              <span>{selectedId ? uiText("Выбранный шаблон сохранится после создания аудита.") : uiText("Выберите один шаблон для продолжения.")}</span>
              <button type="button" className="ap-button ap-button--secondary" onClick={onClose}>{uiText("Закрыть")}</button>
            </footer>
          </>
        )}
    </Dialog>
  );

  return modal;
}

export { isCompatible as isTemplateCompatible };
